'use client';

import Script from 'next/script';

import { useSearchContext } from '@/services/search/SearchProvider';
import useCheckScreen from '@/src/hooks/useCheckScreen';
import {
    useBrowseContext,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

const OneTagSearchPageEventScriptClient = ({
    partnerId,
    categoryId,
    isCategoryPage,
}: {
    partnerId: string;
    categoryId?: string;
    isCategoryPage?: boolean;
}) => {
    const { criteoUserCookie } = useBrowseContext();
    const { isMobile } = useCheckScreen();
    const { queryParams: searchParams } = useQueryParams();
    const { searchResult, isSearchResultsLoading } = useSearchContext();
    const keywordParam = searchParams.get('keyword');
    const currentPageParam = searchParams.get('currentPage');
    const refineParam = searchParams.get('refine');

    if (!criteoUserCookie || isSearchResultsLoading) {
        return null;
    }

    const placementKey = isCategoryPage ? 'viewCategory' : 'viewSearch';

    const categoryScriptValue = '';
    const itemsScriptValue = '';

    // NOTE: These values are inserted into a <Script> tag below.
    //       They must be properly escaped to prevent XSS vulnerabilities.
    //       We use JSON.stringify which is safe and handles all edge cases.
    const criteoData: Record<string, unknown> = {
        partnerId,
        criteoUserCookie,
        categoryScriptValue,
        itemsScriptValue,
        keywordParam,
        refineParam,
        currentPageParam,
        category: categoryId,
        isMobile,
        isProd:
            process.env.CONTENT_ENTRY_ENVIRONMENT_CLIENT_CONFIGURATION ===
            'prod',
        pageId: isMobile
            ? `${placementKey}Offsite_mobile`
            : `${placementKey}Offsite_desktop`,
        eventName: isCategoryPage ? placementKey : 'viewSearchResult',
    };

    if (categoryId) {
        criteoData.category = categoryId;
        let items: string[] = [];
        if (searchResult && 'docs' in searchResult && searchResult.docs) {
            items = searchResult.docs.reduce(
                (
                    itemNumbers: string[],
                    {
                        group_id,
                        item_fsa_eligible,
                        item_chdi_eligible,
                    }: Partial<{
                        group_id?: string;
                        item_fsa_eligible?: boolean;
                        item_chdi_eligible?: boolean;
                    }>,
                ) => {
                    if (
                        itemNumbers.length < 3 &&
                        group_id &&
                        !item_fsa_eligible &&
                        !item_chdi_eligible
                    ) {
                        itemNumbers.push(group_id);
                    }

                    return itemNumbers;
                },
                [] as string[],
            );
        }
        if (items?.length) {
            criteoData.item = items;
        } else {
            criteoData.item = ['None'];
        }
    }

    const serializedCriteoData = JSON.stringify(criteoData).replace(
        /</g,
        '\\u003C',
    );

    return (
        <Script id="CriteoOneTagEventsScript" strategy="lazyOnload">
            {`
              if(typeof window !== "undefined") {
                window.criteo_q = window.criteo_q || [];
                const data = ${serializedCriteoData};

                const deviceType = data.isMobile ? "m" : "d";
                const pageId = data.pageId;

                const pageEvent = {
                    event: data.eventName,
                    page_id: pageId,
                    keyword: data.keywordParam || 'null', 
                    filters: data.refineParam || 'null',
                    page_number: data.currentPageParam || 1
                }

                if (data.category) {
                    pageEvent.category = data.category;
                }
                if (data.item) {
                    pageEvent.item = data.item;
                }

                const criteoEventsArray = [
                  { event: "setAccount", account: data.partnerId},
                  { event: "setEmail", email: data.criteoUserCookie, hash_method: "sha256" },
                  { event: "setSiteType", type: deviceType},
                  { event: "setRetailerVisitorId", id: data.criteoUserCookie},
                  pageEvent
                ];

                if (data.isProd) {
                  criteoEventsArray.push({ event: "setUat", uat: "1"});
                }
                window.criteo_q.push(criteoEventsArray);
              };
            `}
        </Script>
    );
};

export default OneTagSearchPageEventScriptClient;
